import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchProduct, formatRON, imgUrl, notifyStock } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCategories } from "@/context/CategoriesContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useContent } from "@/context/ContentContext";
import { ext } from "@/lib/ext";
import SizeGuide from "@/components/SizeGuide";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const StockLabel = ({ stock }) => {
  const { t } = useLang();
  if (stock === 0) return <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="stock-status">{t.product.outOfStock}</span>;
  if (stock <= 3) return <span className="font-mono text-[11px] uppercase tracking-widest" data-testid="stock-status">{t.product.lowStock} ({stock})</span>;
  return <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="stock-status">{t.product.inStock}</span>;
};

const NotifyForm = ({ slug }) => {
  const { lang } = useLang();
  const e = ext[lang];
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault();
    if (!email.includes("@")) return;
    try { await notifyStock(slug, email); setDone(true); toast.success(e.notifyOk); } catch { toast.error("Error"); }
  };
  return (
    <form onSubmit={submit} className="mt-7 border border-border p-4" data-testid="notify-form">
      <div className="mb-2 font-mono text-[11px] uppercase tracking-widest">{e.notifyTitle}</div>
      {done ? <p className="text-sm text-muted-foreground">{e.notifyOk}</p> : (
        <div className="flex border border-border">
          <input type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} placeholder={e.notifyPh} data-testid="notify-email"
            className="w-full bg-transparent px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest focus:outline-none" />
          <button type="submit" data-testid="notify-submit" className="bg-foreground px-4 font-mono text-[11px] uppercase tracking-widest text-white">{e.notifyBtn}</button>
        </div>
      )}
    </form>
  );
};

const Product = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { label } = useCategories();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const { has, toggle } = useWishlist();
  const content = useContent();
  const e = ext[lang];
  const { data: product, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: () => fetchProduct(slug) });

  const [colorIdx, setColorIdx] = useState(0);
  const [size, setSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const [zoom, setZoom] = useState(false);

  // Sizes/stock can be overridden per color; empty color sizes = product-level sizes
  const effSizes = useMemo(() => {
    if (!product) return [];
    const c = product.colors?.[colorIdx];
    return (c?.sizes && c.sizes.length) ? c.sizes : product.sizes;
  }, [product, colorIdx]);

  // Auto-select when there is only one size (no need to choose)
  const singleSize = product && effSizes.length === 1;
  useEffect(() => {
    if (singleSize) setSize(effSizes[0].label);
  }, [singleSize, effSizes]);

  // Reset the active image when the color changes; drop the size if the new color doesn't carry it
  useEffect(() => {
    setActiveImg(0);
    setSize((prev) => {
      if (!prev) return prev;
      const s = effSizes.find((x) => x.label === prev);
      return s && s.stock > 0 ? prev : (effSizes.length === 1 ? effSizes[0].label : null);
    });
  }, [colorIdx, effSizes]);

  const selectedSizeObj = useMemo(() => effSizes.find((s) => s.label === size), [effSizes, size]);

  if (isLoading || !product) return <main className="flex h-screen items-center justify-center font-mono text-xs uppercase tracking-widest">LOADING…</main>;

  const color = product.colors[colorIdx];
  const colorImages = (color?.images && color.images.length) ? color.images : product.images;
  const safeActive = Math.min(activeImg, Math.max(0, colorImages.length - 1));
  const prevImg = () => setActiveImg((i) => (Math.min(i, colorImages.length - 1) - 1 + colorImages.length) % colorImages.length);
  const nextImg = () => setActiveImg((i) => (Math.min(i, colorImages.length - 1) + 1) % colorImages.length);
  const name = lang === "ro" ? product.name_ro : product.name_en;
  const description = lang === "ro" ? product.description_ro : product.description_en;
  const totalStock = effSizes.reduce((a, s) => a + s.stock, 0);
  const wished = has(product.slug);

  const handleAdd = () => {
    if (!size) { toast.error(t.product.selectSize); return; }
    addItem({ slug: product.slug, name, size, color: lang === "ro" ? color.name_ro : color.name_en, price: product.price, qty: 1, image: colorImages[0] });
    toast.success(`${t.product.added} — ${name}`);
  };

  const handleBuyNow = () => {
    if (!size) { toast.error(t.product.selectSize); return; }
    addItem({ slug: product.slug, name, size, color: lang === "ro" ? color.name_ro : color.name_en, price: product.price, qty: 1, image: colorImages[0] });
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <main data-testid="product-page">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="border-b border-border lg:border-b-0 lg:border-r">
          <div className="lg:sticky lg:top-[52px]">
            <div className="group relative flex aspect-square w-full items-center justify-center overflow-hidden bg-white p-10 sm:p-20">
              <motion.img key={safeActive} initial={{ opacity: 0.3 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                onClick={() => setZoom(true)} src={imgUrl(colorImages[safeActive])} alt={name}
                className="max-h-[85%] max-w-[85%] cursor-zoom-in object-contain" data-testid="product-main-image" />
              <button onClick={() => setZoom(true)} data-testid="zoom-open" className="absolute bottom-4 right-4 opacity-40 transition-opacity hover:opacity-100"><ZoomIn size={18} /></button>
              {colorImages.length > 1 && (
                <>
                  <button onClick={prevImg} data-testid="image-prev" aria-label="Previous image"
                    className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center border border-border bg-white/80 backdrop-blur transition-colors hover:bg-white">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={nextImg} data-testid="image-next" aria-label="Next image"
                    className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center border border-border bg-white/80 backdrop-blur transition-colors hover:bg-white">
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            {colorImages.length > 1 && (
              <div className="flex flex-wrap gap-2 border-t border-border p-3">
                {colorImages.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                    className={`flex h-20 w-20 items-center justify-center overflow-hidden border bg-white p-1.5 transition-opacity ${safeActive === i ? "border-foreground opacity-100" : "border-border opacity-50 hover:opacity-100"}`}>
                    <img src={imgUrl(img)} alt="" className="max-h-full max-w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col p-5 sm:p-10">
          <Link to="/" className="mb-6 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:opacity-60">← {t.nav.shop}</Link>

          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl uc font-semibold leading-tight sm:text-3xl" data-testid="product-name">{name}</h1>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{product.code} · {label(product.category, lang)}</div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => toggle(product.slug)} data-testid="wishlist-toggle" aria-label="wishlist" className="opacity-70 hover:opacity-100">
                <Heart size={20} fill={wished ? "#0a0a0a" : "none"} />
              </button>
              <span className="whitespace-nowrap font-mono text-xl" data-testid="product-price">
                {content?.sales_enabled && product.old_price && product.old_price > product.price ? (
                  <>
                    <span className="mr-2 text-base text-muted-foreground line-through" data-testid="product-old-price">{formatRON(product.old_price)}</span>
                    <span className="text-red-600">{formatRON(product.price)}</span>
                  </>
                ) : formatRON(product.price)}
              </span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3">
            {content?.sales_enabled && (product.sale || (product.old_price && product.old_price > product.price)) && <span className="bg-red-600 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white" data-testid="sale-tag">SALE!</span>}
            <StockLabel stock={selectedSizeObj ? selectedSizeObj.stock : totalStock} />
          </div>

          <div className="mt-7">
            <div className="mb-2 font-mono text-[11px] uppercase tracking-widest">{t.product.colors}: <span className="text-muted-foreground">{lang === "ro" ? color.name_ro : color.name_en}</span></div>
            <div className="flex gap-2.5" data-testid="color-options">
              {product.colors.map((c, i) => (
                <button key={i} onClick={() => setColorIdx(i)} title={lang === "ro" ? c.name_ro : c.name_en} data-testid={`color-${i}`}
                  className={`h-8 w-8 border transition-transform ${colorIdx === i ? "border-foreground ring-1 ring-foreground ring-offset-2 ring-offset-white" : "border-border hover:scale-105"}`} style={{ backgroundColor: c.hex }} />
              ))}
            </div>
          </div>

          <div className="mt-7">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-widest">{t.product.sizes}</span>
              {product.show_size_guide !== false && (
                <button onClick={() => setGuideOpen(true)} data-testid="size-guide-btn" className="font-mono text-[11px] uppercase tracking-widest underline underline-offset-4 hover:opacity-60">{t.product.sizeGuide}</button>
              )}
            </div>
            {singleSize ? (
              <div className="border border-border px-3 py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground" data-testid="single-size">
                {(lang === "ro" ? "Mărime unică" : "One size")}: {effSizes[0].label}
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2" data-testid="size-options">
                {effSizes.map((s) => {
                  const disabled = s.stock === 0;
                  const active = size === s.label;
                  return (
                    <button key={s.label} disabled={disabled} onClick={() => setSize(s.label)} data-testid={`size-${s.label}`}
                      className={`border py-3 font-mono text-xs uppercase tracking-wider transition-colors ${active ? "border-foreground bg-foreground text-white" : "border-border hover:border-foreground"} ${disabled ? "cursor-not-allowed text-muted-foreground line-through opacity-40 hover:border-border" : ""}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button onClick={handleAdd} data-testid="add-to-cart" className="mt-7 w-full border border-foreground bg-white py-4 text-sm uc font-medium text-foreground transition-colors hover:bg-secondary">
            {t.product.addToCart}
          </button>

          {totalStock > 0 && (
            <button onClick={handleBuyNow} data-testid="buy-now" className="mt-2 flex w-full items-center justify-center gap-2 bg-foreground py-4 text-sm uc font-medium text-white transition-opacity hover:opacity-85">
              {lang === "ro" ? "Cumpără acum" : "Buy now"}
              <span className="font-mono text-[10px] normal-case tracking-normal opacity-70">· Card · Apple Pay · Google Pay</span>
            </button>
          )}

          {totalStock === 0 && <NotifyForm slug={product.slug} />}

          <div className="mt-7 border-t border-border pt-4" data-testid="product-details">
            <div className="font-mono text-[11px] uppercase tracking-widest">{t.product.details}</div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
          </div>

        </div>
      </div>

      <SizeGuide open={guideOpen} onOpenChange={setGuideOpen} category={product.category} />
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-3xl border border-border bg-white p-2" data-testid="zoom-modal">
          <DialogTitle className="sr-only">{name}</DialogTitle>
          <img src={imgUrl(colorImages[safeActive])} alt={name} className="max-h-[80vh] w-full object-contain" />
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Product;
